/** Browser-side, anonymous correlation for Calendly. It never invents an identity. */
const BOOKING_CONTEXT_URL = "https://revenue-api.topgeo.ai/v1/booking-contexts";
const CACHE_KEY = "topgeo.booking-context.v1";
const FIRST_TOUCH_KEY = "topgeo.booking-first-touch.v1";
const LAST_TOUCH_KEY = "topgeo.booking-last-touch.v1";
const MAX_VALUE = 200;
const MAX_URL = 2048;
const MAX_STORAGE_BYTES = 8192;
const REQUEST_TIMEOUT_MS = 650;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const TOKEN = /^tgctx_[A-Za-z0-9_-]{32}$/;

export type BookingTouch = { timestamp: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string; referrer?: string; landing_url?: string };
type CachedContext = { cache_key: string; token: string; expires_at: string; distinct_id?: string; utm_content?: string };
export type BookingContext = { token: string; expiresAt: string; distinctId?: string; utmContent?: string };
/** `null` means opted out; `undefined` means analytics is unavailable. */
export type BookingContextOptions = { reportSlug?: string | null; distinctId?: string | null; getDistinctId?: () => string | null | undefined; timeoutMs?: number };
type PosthogIdentitySource = { __loaded?: boolean; has_opted_out_capturing?: () => boolean; get_distinct_id?: () => unknown };
const inFlight = new Map<string, Promise<BookingContext | null>>();

export function bookingContextEnabled(): boolean { return process.env.NEXT_PUBLIC_BOOKING_CONTEXT_ENABLED === "true"; }
function utf8Bytes(value: string): number { return typeof TextEncoder === "undefined" ? value.length : new TextEncoder().encode(value).byteLength; }
function safeText(value: unknown, max = MAX_VALUE): string | undefined {
  if (typeof value !== "string" || !value.trim() || /[\u0000-\u001f\u007f]/.test(value) || utf8Bytes(value) > max) return undefined;
  return value;
}
function validTimestamp(value: unknown): value is string { return typeof value === "string" && /(?:Z|[+-]\d\d:\d\d)$/i.test(value) && Number.isFinite(Date.parse(value)); }

/** Keep only acquisition UTMs. Unknown URL query data is never persisted. */
export function sanitizeAttributionUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || /[\u0000-\u001f\u007f]/.test(value) || utf8Bytes(value) > MAX_URL) return undefined;
  try {
    const url = new URL(value);
    if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password) return undefined;
    const params = new URLSearchParams();
    for (const key of UTM_KEYS) { const utm = safeText(url.searchParams.get(key)); if (utm) params.set(key, utm); }
    url.search = params.toString(); url.hash = "";
    const sanitized = url.toString();
    return utf8Bytes(sanitized) <= MAX_URL ? sanitized : undefined;
  } catch { return undefined; }
}
/** Legacy PostHog ids can be PII. B1 accepts only opaque, bounded ids. */
export function safeAnonymousDistinctId(value: unknown): string | undefined {
  const id = safeText(value, MAX_VALUE);
  return id && !/[\s@]/.test(id) ? id : undefined;
}
/** Reads only a ready, opted-in PostHog browser id. It does not create one. */
export function consentedAnonymousDistinctId(posthog: PosthogIdentitySource): string | null | undefined {
  try {
    if (!posthog?.__loaded) return undefined;
    if (posthog.has_opted_out_capturing?.()) return null;
    return safeAnonymousDistinctId(posthog.get_distinct_id?.());
  } catch { return undefined; }
}

function currentTouch(now = new Date()): BookingTouch {
  if (typeof window === "undefined") return { timestamp: now.toISOString() };
  const params = new URLSearchParams(window.location.search); const touch: BookingTouch = { timestamp: now.toISOString() };
  for (const key of UTM_KEYS) { const value = safeText(params.get(key)); if (value) touch[key] = value; }
  const referrer = typeof document === "undefined" ? undefined : sanitizeAttributionUrl(document.referrer);
  const landingUrl = sanitizeAttributionUrl(window.location.href);
  if (referrer) touch.referrer = referrer; if (landingUrl) touch.landing_url = landingUrl;
  return touch;
}
function reconstructTouch(value: unknown): BookingTouch | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>; if (!validTimestamp(raw.timestamp)) return null;
  const touch: BookingTouch = { timestamp: raw.timestamp };
  for (const key of UTM_KEYS) { const item = raw[key]; if (item !== undefined) { const safe = safeText(item); if (!safe || safe !== item) return null; touch[key] = safe; } }
  for (const key of ["referrer", "landing_url"] as const) { const item = raw[key]; if (item !== undefined) { const safe = sanitizeAttributionUrl(item); if (!safe || safe !== item) return null; touch[key] = safe; } }
  return touch;
}
function readSession(key: string): unknown | null { try { const raw = window.sessionStorage.getItem(key); return !raw || utf8Bytes(raw) > MAX_STORAGE_BYTES ? null : JSON.parse(raw); } catch { return null; } }
function writeSession(key: string, value: unknown): void { try { const encoded = JSON.stringify(value); if (utf8Bytes(encoded) <= MAX_STORAGE_BYTES) window.sessionStorage.setItem(key, encoded); } catch { /* storage blockers fail open */ } }
function touches(): { firstTouch: BookingTouch; lastTouch: BookingTouch } {
  const observedTouch = currentTouch();
  const storedFirst = reconstructTouch(typeof window === "undefined" ? null : readSession(FIRST_TOUCH_KEY));
  const firstTouch = storedFirst ?? observedTouch; if (!storedFirst && typeof window !== "undefined") writeSession(FIRST_TOUCH_KEY, firstTouch);
  const previousLast = reconstructTouch(typeof window === "undefined" ? null : readSession(LAST_TOUCH_KEY));
  const lastTouch = previousLast?.landing_url === observedTouch.landing_url && previousLast?.referrer === observedTouch.referrer ? previousLast : observedTouch;
  if (lastTouch === observedTouch && typeof window !== "undefined") writeSession(LAST_TOUCH_KEY, lastTouch);
  return { firstTouch, lastTouch };
}
function cacheKey(reportSlug: string | undefined, distinctId: string | undefined, firstTouch: BookingTouch, lastTouch: BookingTouch): string { return JSON.stringify({ reportSlug: reportSlug ?? "", distinctId: distinctId ?? "", firstTouch, lastTouch }); }
function unexpired(value: unknown, key: string): BookingContext | null {
  if (!value || typeof value !== "object") return null;
  const entry = value as Record<string, unknown>;
  if (entry.cache_key !== key || typeof entry.token !== "string" || !TOKEN.test(entry.token) || !validTimestamp(entry.expires_at) || Date.parse(entry.expires_at) <= Date.now()) return null;
  const distinctId = entry.distinct_id === undefined ? undefined : safeAnonymousDistinctId(entry.distinct_id);
  const utmContent = entry.utm_content === undefined ? undefined : safeText(entry.utm_content);
  if ((entry.distinct_id !== undefined && distinctId !== entry.distinct_id) || (entry.utm_content !== undefined && utmContent !== entry.utm_content)) return null;
  return { token: entry.token, expiresAt: entry.expires_at, distinctId, utmContent };
}
/** Re-check a token at navigation time without issuing another request. */
export function bookingContextIsCurrent(context: BookingContext | null, identity: string | null | undefined): boolean {
  if (!context || identity === null || !TOKEN.test(context.token) || !validTimestamp(context.expiresAt) || Date.parse(context.expiresAt) <= Date.now()) return false;
  return context.distinctId === safeAnonymousDistinctId(identity);
}
function currentInput(options: BookingContextOptions) {
  const rawIdentity = options.getDistinctId ? options.getDistinctId() : options.distinctId;
  // An opted-out visitor must not create even a touch-storage entry.
  if (rawIdentity === null) {
    return { optedOut: true, reportSlug: undefined, distinctId: undefined, firstTouch: { timestamp: "" }, lastTouch: { timestamp: "" }, key: "" };
  }
  const reportSlug = safeText(options.reportSlug); const distinctId = safeAnonymousDistinctId(rawIdentity); const { firstTouch, lastTouch } = touches();
  return { optedOut: false, reportSlug, distinctId, firstTouch, lastTouch, key: cacheKey(reportSlug, distinctId, firstTouch, lastTouch) };
}

/** Races fetch plus JSON parsing, so abort-ignoring and hanging bodies still fail open. */
function requestJsonWithTimeout(input: Record<string, unknown>, timeoutMs: number): Promise<unknown | null> {
  return new Promise((resolve) => {
    const controller = typeof AbortController === "undefined" ? undefined : new AbortController(); let settled = false;
    const finish = (value: unknown | null) => { if (settled) return; settled = true; clearTimeout(timer); resolve(value); };
    const timer = setTimeout(() => { controller?.abort(); finish(null); }, timeoutMs);
    try { Promise.resolve(fetch(BOOKING_CONTEXT_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input), ...(controller ? { signal: controller.signal } : {}) })).then(
      (response) => { if (response.status !== 201) { finish(null); return; } Promise.resolve(response.json()).then(finish, () => finish(null)); },
      () => finish(null)
    ); } catch { finish(null); }
  });
}

/** Bounded, de-duplicated minting. Errors, opt-out and storage failures return null. */
export async function prepareBookingContext(options: BookingContextOptions = {}): Promise<BookingContext | null> {
  if (!bookingContextEnabled() || typeof window === "undefined") return null;
  let input;
  try { input = currentInput(options); } catch { return null; }
  if (input.optedOut) return null;
  const cached = unexpired(readSession(CACHE_KEY), input.key); if (cached) return cached;
  const existing = inFlight.get(input.key); if (existing) return existing;
  const pending = (async (): Promise<BookingContext | null> => {
    try {
      const body = { ...(input.distinctId ? { distinct_id: input.distinctId } : {}), ...(input.reportSlug ? { report_slug: input.reportSlug } : {}), first_touch: input.firstTouch, last_touch: input.lastTouch };
      if (utf8Bytes(JSON.stringify(body)) > MAX_STORAGE_BYTES) return null;
      const result = await requestJsonWithTimeout(body, Math.max(100, Math.min(options.timeoutMs ?? REQUEST_TIMEOUT_MS, REQUEST_TIMEOUT_MS)));
      if (!result || typeof result !== "object") return null;
      const response = result as Record<string, unknown>;
      const entry: CachedContext = { cache_key: input.key, token: response.token as string, expires_at: response.expires_at as string, ...(input.distinctId ? { distinct_id: input.distinctId } : {}), ...(input.lastTouch.utm_content ? { utm_content: input.lastTouch.utm_content } : {}) };
      const context = unexpired(entry, input.key); const latest = currentInput(options);
      if (!context || latest.optedOut || latest.key !== input.key) return null;
      writeSession(CACHE_KEY, entry); return context;
    } catch { return null; } finally { inFlight.delete(input.key); }
  })();
  inFlight.set(input.key, pending); return pending;
}
/** Preserve all existing Calendly URL data except the reserved correlation slot. */
export function calendlyUrlWithBookingContext(url: string, context: BookingContext | null): string {
  if (!context || !TOKEN.test(context.token)) return url;
  try {
    const target = new URL(url);
    if (target.origin !== "https://calendly.com" || target.username || target.password) return url;
    target.searchParams.set("utm_term", context.token);
    return target.toString();
  } catch { return url; }
}
